import { useTranslation } from "react-i18next";
import { cn } from "../lib/cn";
import { SUPPORTED_LANGS, type Lang } from "../lib/i18n";

/**
 * Compact NL/EN segmented toggle. Flips the active language at runtime via
 * `i18n.changeLanguage` (persisted to localStorage by the detector). Sized to
 * sit alongside <ThemeToggle/> (h-8) in the topbar and landing nav.
 */
export function LanguageSwitcher({ className }: { className?: string }) {
  const { i18n, t } = useTranslation();
  const current = (SUPPORTED_LANGS.find((l) => i18n.resolvedLanguage === l) ?? "nl") as Lang;

  return (
    <div
      role="group"
      aria-label={t("lang.label")}
      className={cn(
        "inline-flex h-8 items-center gap-0.5 rounded-md border border-line bg-surface-2 px-0.5",
        className,
      )}
    >
      {SUPPORTED_LANGS.map((lng) => {
        const active = current === lng;
        return (
          <button
            key={lng}
            type="button"
            onClick={() => void i18n.changeLanguage(lng)}
            aria-pressed={active}
            title={t(`lang.${lng}`)}
            className={cn(
              "ring-focus flex h-7 items-center rounded px-2 text-xs font-semibold uppercase transition-colors",
              active ? "bg-surface-0 text-ink" : "text-ink-faint hover:text-ink",
            )}
          >
            {lng}
          </button>
        );
      })}
    </div>
  );
}
